import { useState } from "react";
import { Calculator, Lock, Target } from "lucide-react";
import { Input } from "../../components/ui/input";

import { fmtINR, fmtL, fmtPct, fmtNum, decisionTone } from "./utils";

// Display-only Indian-numbering integer formatter for the DOC Monthly-cost
// column. Purely presentational — state always holds the raw number.
const fmtINCommas = (n) =>
  n == null || n === "" || isNaN(+n)
    ? ""
    : Math.round(+n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// Inline helper: tone color for the "Current rate · MIS" column. Pulled out of the
// row map below so the ternary chain doesn't read as a 4-level deep expression.
function rateTone(misRate, delta) {
  if (!misRate)              return "text-zinc-400";
  if (Math.abs(delta) < 10)  return "text-emerald-700";
  if (delta > 0)             return "text-rose-700";
  return "text-amber-700";
}

// =====================================================================
// P&L primitives — each of the sub-sections below is exported so
// PropertyIC.jsx can compose them into the new row-based layout.
// All state remains external; these are dumb view components.
// =====================================================================

/** 8-tile grid (3 wide × 3 tall — bottom-right slot reserved / empty) rendered
 *  in the right column of Row 2. Visual style matches the pre-Phase-C1 P&L
 *  tiles (small uppercase label, large bold value, subtext, colored accent
 *  underline). Sits above the Revenue 2×2 block; natural heights are
 *  intentionally NOT forced to match Estimates on the left. */
export function KpiStrip({ sc }) {
  const rows = [
    ["Dorm Occ",       fmtPct(sc?.dorm_occ_pct),   "occupancy",             "kpi-dorm-occ",       "#FF69B4"],
    ["Dorm ADR",       fmtINR(sc?.dorm_adr),       "per bed/night",         "kpi-dorm-adr",       "#FF69B4"],
    ["Private Occ",    fmtPct(sc?.private_occ_pct),"occupancy",             "kpi-private-occ",    "#9370DB"],
    ["Private ADR",    fmtINR(sc?.private_adr),    "per room/night",        "kpi-private-adr",    "#9370DB"],
    ["Dorm RevPAR",    fmtINR(sc?.dorm_revpar),    "per dorm room/day",     "kpi-dorm-revpar",    "#FF69B4"],
    ["Private RevPAR", fmtINR(sc?.private_revpar), "per private room/day",  "kpi-private-revpar", "#9370DB"],
    ["RevPAB",         fmtINR(sc?.revpab),         "rooms + F&B",           "kpi-revpab",         "#20B2AA"],
    ["F&B RevPAB",     fmtINR(sc?.non_bed_revpab), "₹ × occ% per bed/day",  "kpi-fnb-revpab",     "#FFCC00"],
  ];
  return (
    <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid="ic-kpi-strip">
      <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50/70 flex items-center gap-2">
        <Calculator className="w-3.5 h-3.5 text-brand-purple" />
        <span className="font-display font-bold text-[12.5px] text-zinc-900">P&amp;L</span>
      </div>
      <div className="p-2 grid grid-cols-3 gap-2">
        {rows.map(([label, value, sub, testid, accent]) => (
          <div key={testid} className="bg-zinc-50/60 border border-zinc-200/80 rounded-lg p-2.5 min-w-0" data-testid={testid}>
            <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 truncate">{label}</div>
            <div className="font-display font-black text-[17px] leading-tight text-zinc-900 mt-0.5 tabular-nums">{value}</div>
            <div className="text-[10px] text-zinc-400 mt-0.5 truncate">{sub}</div>
            <div className="mt-1.5 h-[3px] w-7 rounded-full" style={{ background: accent }} />
          </div>
        ))}
        {/* Reserved 9th slot (Row 3, Col 3) — intentionally empty for a future tile. */}
        <div aria-hidden="true" data-testid="kpi-reserved-slot" />
      </div>
    </div>
  );
}

/** 4-cell revenue block arranged 2 wide × 2 tall so it fits in the
 *  half-width right column of Row 2 (below the P&L tile grid). */
export function RevenueBand({ sc, rs, params }) {
  return (
    <div className="rounded-lg border border-emerald-200 bg-white overflow-hidden">
      <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 text-emerald-900 font-display font-bold text-[12px] uppercase tracking-wide">Revenue</div>
      <div className="grid grid-cols-2">
        <div className="p-3 border-r border-b border-emerald-100 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-medium">Dorm income</div>
          <div className="font-display font-black text-[16px] tabular-nums text-emerald-900 mt-0.5" data-testid="ic-dorm-revenue">{fmtINR(sc?.dorm_revenue)}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5 truncate">{rs.sellable_dorm_beds ?? rs.dorm_beds} beds × {fmtPct(sc?.dorm_occ_pct)} × ₹{Math.round(sc?.dorm_adr || 0)}</div>
        </div>
        <div className="p-3 border-b border-emerald-100 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-medium">Private income</div>
          <div className="font-display font-black text-[16px] tabular-nums text-emerald-900 mt-0.5" data-testid="ic-private-revenue">{fmtINR(sc?.private_revenue)}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5 truncate">{rs.sellable_private_rooms ?? rs.private_rooms} rooms × {fmtPct(sc?.private_occ_pct)} × ₹{Math.round(sc?.private_adr || 0)}</div>
        </div>
        <div className="p-3 border-r border-emerald-100 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-medium">F&B</div>
          <div className="font-display font-black text-[16px] tabular-nums text-emerald-900 mt-0.5" data-testid="ic-fnb-revenue">{fmtINR(sc?.fnb_revenue)}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5 truncate">{fmtNum(sc?.total_sold_bed_nights)} bed-nts × ₹{params.fnb_per_bed_per_day}</div>
        </div>
        <div className="p-3 bg-emerald-50/40 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-medium">Total revenue</div>
          <div className="font-display font-black text-[16px] tabular-nums text-emerald-900 mt-0.5" data-testid="ic-total-revenue">{fmtINR(sc?.total_revenue)}</div>
          <div className="text-[10px] text-emerald-700 mt-0.5 truncate">Dorm + Private + F&B</div>
        </div>
      </div>
    </div>
  );
}

/** Prod-style vertical Direct & Operational Cost table. 14 rows × 5 cols. */
export function OpexTable({ sc, opOverrides, setOpOverrides, misRates, params }) {
  // Which Monthly-cost cell is currently focused — used to switch between
  // comma-formatted display (idle) and raw digits (editing).
  const [focusedRow, setFocusedRow] = useState(null);
  return (
    <div className="rounded-lg border border-zinc-200 overflow-hidden">
      <div className="px-3 py-2 bg-rose-50 border-b border-rose-200 text-rose-900 font-display font-bold text-[12px] uppercase tracking-wide flex items-center justify-between">
        <span>Direct & Operational Cost <span className="text-rose-600 normal-case font-normal text-[10.5px]">(editable per property; matrix unchanged)</span></span>
        <div className="flex items-center gap-2">
          {Object.keys(opOverrides).length > 0 && (
            <button
              onClick={() => setOpOverrides({})}
              data-testid="ic-opex-reset"
              className="text-rose-700 text-[10.5px] font-medium normal-case tracking-normal hover:underline"
            >
              Reset to matrix
            </button>
          )}
          <span className="text-rose-700 text-[10.5px] font-medium normal-case tracking-normal">₹/bed/day · monthly</span>
        </div>
      </div>
      <table className="w-full text-[12.5px]" data-testid="ic-pl-cost-breakup">
        <thead>
          <tr className="bg-rose-50/60 border-b border-rose-200 text-[9.5px] uppercase tracking-wide text-rose-700 font-bold">
            <th className="px-3 py-1.5 text-left font-bold">Line item</th>
            <th className="px-2 py-1.5 text-right font-bold w-[105px]">Cost / bed / day</th>
            <th className="px-2 py-1.5 text-right font-bold w-[80px]" title="Share of Total Revenue">% of Rev</th>
            <th className="px-2 py-1.5 text-right font-bold w-[130px]" title={`Latest live ₹/bed/day across operational ${params.type} stops${misRates.sample_size ? ` (${misRates.sample_size} props)` : ""} — display only`}>
              Current rate · MIS
            </th>
            <th className="px-2 py-1.5 text-right font-bold w-[160px]">Monthly cost (editable)</th>
          </tr>
        </thead>
        <tbody>
          {sc?.cost_breakdown && Object.entries(sc.cost_breakdown).map(([li, val], i) => {
            const perDay = sc.cost_breakdown_per_bed_day?.[li] || 0;
            const isOverride = li in opOverrides;
            const misRate = misRates.rates?.[li] ?? 0;
            const delta = misRate && perDay ? ((perDay - misRate) / misRate * 100) : 0;
            const tone = rateTone(misRate, delta);
            const pctOfRev = sc?.total_revenue ? (val / sc.total_revenue * 100) : 0;
            return (
              <tr key={li} className={`border-b border-zinc-100 ${i % 2 ? "bg-zinc-50/40" : ""}`}>
                <td className="px-3 py-1.5 text-zinc-700">
                  {li}
                  {isOverride && <span className="ml-1.5 text-[9.5px] uppercase tracking-wide font-bold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1 py-px" data-testid={`ic-opex-edited-${li}`}>edited</span>}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-zinc-500 text-[11px]">₹{perDay.toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-[11px] text-zinc-700 font-medium" data-testid={`ic-opex-pctrev-${li}`}>{pctOfRev.toFixed(1)}%</td>
                <td className={`px-2 py-1.5 text-right tabular-nums text-[11px] ${tone}`} data-testid={`ic-opex-mis-rate-${li}`} title={misRate ? `MIS avg for ${params.type}: ₹${misRate}/bed/day · projection ${delta > 0 ? "+" : ""}${delta.toFixed(0)}% vs MIS` : "No MIS data"}>
                  {misRate ? `₹${misRate.toFixed(2)}` : "—"}
                  {misRate && Math.abs(delta) >= 10 ? <span className="ml-0.5 text-[9px]">{delta > 0 ? "↑" : "↓"}{Math.abs(delta).toFixed(0)}%</span> : null}
                </td>
                <td className="px-2 py-1 text-right">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={focusedRow === li
                      ? String(Math.round(isOverride ? opOverrides[li] : val) || "")
                      : fmtINCommas(isOverride ? opOverrides[li] : val)}
                    onChange={(e) => {
                      // Strip any non-digits (pasted commas, stray spaces) — we
                      // always persist a raw integer to state.
                      const raw = e.target.value.replace(/[^0-9]/g, "");
                      if (raw === "") {
                        setOpOverrides((o) => { const n = { ...o }; delete n[li]; return n; });
                      } else {
                        setOpOverrides((o) => ({ ...o, [li]: +raw }));
                      }
                    }}
                    onFocus={(e) => { setFocusedRow(li); e.target.select(); }}
                    onBlur={() => setFocusedRow(null)}
                    onKeyDown={(e) => {
                      // Preserve native <input type=number> arrow-key stepping
                      // (increment/decrement by 1) since we switched to type=text
                      // to allow the comma-formatted display value.
                      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                        e.preventDefault();
                        const delta = e.key === "ArrowUp" ? 1 : -1;
                        const cur = Number(isOverride ? opOverrides[li] : val) || 0;
                        const next = Math.max(0, Math.round(cur + delta));
                        setOpOverrides((o) => ({ ...o, [li]: next }));
                      }
                    }}
                    className={`h-7 text-right tabular-nums text-[12px] rounded ${isOverride ? "border-amber-300 bg-amber-50 text-amber-900" : "border-zinc-200 text-rose-700"}`}
                    data-testid={`ic-opex-input-${li}`}
                  />
                </td>
              </tr>
            );
          })}
          <tr className="bg-rose-50">
            <td className="px-3 py-2 font-display font-bold text-rose-900">Total Operating Expense</td>
            <td className="px-2 py-2"></td>
            <td className="px-2 py-2 text-right tabular-nums font-display font-bold text-rose-900 text-[12.5px]" data-testid="ic-total-opex-pctrev-inline">
              {sc?.total_revenue ? ((sc.operational_cost / sc.total_revenue) * 100).toFixed(1) : "0.0"}%
            </td>
            <td className="px-2 py-2 text-right tabular-nums text-[10.5px] text-rose-700" title="MIS sum across all line items × bed-nights">
              {misRates.sample_size > 0 ? `MIS · ${misRates.sample_size} stops` : "—"}
            </td>
            <td className="px-3 py-2 text-right tabular-nums font-display font-bold text-rose-900" data-testid="ic-total-opex-inline">{fmtINR(sc?.operational_cost)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Row 4 — 6-card compact horizontal financial totals strip. */
export function FinancialTotalsStrip({ sc, rs, misRates }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="ic-financial-totals-strip">
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-2.5">
        <div className="text-[9.5px] uppercase font-medium tracking-wide text-rose-700">Total Op Expense</div>
        <div className="font-display font-black text-[15px] text-rose-900 mt-0.5 tabular-nums" data-testid="ic-total-opex">{fmtINR(sc?.operational_cost)}</div>
      </div>
      <div className="rounded-lg border border-rose-200 bg-white p-2.5">
        <div className="text-[9.5px] uppercase font-medium tracking-wide text-rose-700">Opex % of Revenue</div>
        <div className="font-display font-black text-[15px] text-rose-900 mt-0.5 tabular-nums" data-testid="ic-total-opex-pctrev">
          {sc?.total_revenue ? ((sc.operational_cost / sc.total_revenue) * 100).toFixed(1) : "0.0"}%
        </div>
      </div>
      <div className="rounded-lg border border-rose-200 bg-white p-2.5">
        <div className="text-[9.5px] uppercase font-medium tracking-wide text-rose-700">MIS Benchmark</div>
        <div className="font-display font-black text-[13px] text-rose-800 mt-0.5">
          {misRates.sample_size > 0 ? `${misRates.sample_size} stops` : "—"}
        </div>
        <div className="text-[9.5px] text-rose-600 mt-0.5">Live network avg</div>
      </div>
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5">
        <div className="text-[9.5px] uppercase font-medium tracking-wide text-emerald-700">Hostel-Level Profit</div>
        <div className="flex items-baseline gap-2">
          <div className="font-display font-black text-[15px] text-emerald-900 mt-0.5 tabular-nums" data-testid="ic-hostel-profit">{fmtINR(sc?.hostel_level_profit)}</div>
          <div className="text-[11px] font-display font-black text-emerald-800" data-testid="ic-hostel-profit-pct">{fmtPct(sc?.hostel_level_profit_pct)}</div>
        </div>
      </div>
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5">
        <div className="text-[9.5px] uppercase font-medium tracking-wide text-amber-700">Central Cost</div>
        <div className="font-display font-black text-[15px] text-amber-900 mt-0.5 tabular-nums" data-testid="ic-central-cost">{fmtINR(sc?.central_cost)}</div>
        <div className="text-[9.5px] text-amber-700 mt-0.5">₹50/bed/day × {rs.total_beds} × 30</div>
      </div>
      <div className="rounded-lg border border-zinc-800 bg-zinc-900 text-white p-2.5">
        <div className="text-[9.5px] uppercase font-medium tracking-wide text-zinc-400">EBITDA / month</div>
        <div className="flex items-baseline gap-2">
          <div className="font-display font-black text-[16px] tabular-nums" data-testid="ic-ebitda">{fmtINR(sc?.ebitda)}</div>
          <div className="text-[11px] font-display font-black text-emerald-300" data-testid="ic-ebitda-pct">{fmtPct(sc?.ebitda_margin_pct)}</div>
        </div>
        <div className="text-[9.5px] text-zinc-400 mt-0.5">Yearly Inflow {fmtL(sc?.yearly_inflow)}</div>
      </div>
    </div>
  );
}

/** Row 4 — compact horizontal decision metrics strip.
 *  Capex · ROI/Decision · Break-even Rent (max) · Min Rent. */
export function DecisionMetricsStrip({ sc, rs, breakeven, model }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2" data-testid="ic-decision-metrics-strip">
      <div className="rounded-lg border-2 border-violet-300 bg-violet-50 p-2.5">
        <div className="text-[9.5px] uppercase font-medium tracking-wide text-violet-700">Capex</div>
        <div className="font-display font-black text-[18px] text-violet-900 mt-0.5 tabular-nums" data-testid="ic-capex-final">{fmtL(sc?.capex)}</div>
        <div className="text-[9.5px] text-violet-700 mt-0.5">{rs.total_beds} beds</div>
      </div>
      <div className={`p-2.5 rounded-lg border-2 ${decisionTone(sc?.decision_recommendation)}`} data-testid="ic-decision-recommendation">
        <div className="text-[9.5px] uppercase font-medium tracking-wide opacity-70">ROI / Decision</div>
        <div className="flex items-baseline gap-2">
          <div className="font-display font-black text-[18px] mt-0.5 leading-tight tabular-nums">{sc?.roi_months ? `${sc.roi_months} mo` : "—"}</div>
          <div className="text-[12px] font-bold">{sc?.decision_recommendation || "—"}</div>
        </div>
        <div className="text-[9.5px] mt-0.5 opacity-70">≤36 ✓ · 36-48 ◯ · &gt;48 ✗</div>
      </div>
      <RentCard title="Break-even rent · 36-mo ROI" tag="Max" data={breakeven} model={model} testid="ic-breakeven-rent" showCurrent={breakeven} />
      <RentCard title="Min rent · 36-mo ROI" tag="Min" data={breakeven?.min_rent} model={model} testid="ic-min-rent" />
    </div>
  );
}

const fmtCapexPerBed = (v) => (v >= 100000 ? `₹${(v / 100000).toFixed(v % 100000 ? 1 : 0)}L` : `₹${Math.round(v / 1000)}K`);

/** Assumption line shown under each rent figure, e.g. "Max · COCO ₹80K/bed · central ₹50/bed/day". */
export const rentAssumption = (tag, data, model) =>
  `${tag} · ${model} ${fmtCapexPerBed(data.capex_per_bed || 0)}/bed · central ₹${data.central_cost_per_bed_per_day}/bed/day`;

function RentCard({ title, tag, data, model, testid, showCurrent }) {
  if (!data) {
    return (
      <div className="p-2.5 rounded-lg border-2 border-zinc-200 bg-zinc-50 opacity-60" data-testid={`${testid}-empty`}>
        <div className="text-[9.5px] uppercase font-medium tracking-wide text-zinc-500">{title}</div>
        <div className="font-display font-black text-[13px] text-zinc-500 mt-0.5">—</div>
      </div>
    );
  }
  const ok = data.feasible;
  return (
    <div className={`p-2.5 rounded-lg border-2 ${ok ? "bg-indigo-50 border-indigo-300" : "bg-rose-50 border-rose-300"}`} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <Target className={`w-3.5 h-3.5 ${ok ? "text-indigo-700" : "text-rose-700"}`} />
        <div className={`text-[9.5px] font-medium uppercase tracking-wide ${ok ? "text-indigo-700" : "text-rose-700"}`}>{title}</div>
      </div>
      {ok ? (
        <>
          <div className="font-display font-black text-[16px] text-indigo-900 tabular-nums mt-0.5" data-testid={`${testid}-monthly`}>
            {fmtINR(data.suggested_rent_monthly)}/mo
          </div>
          <div className="text-[9.5px] font-medium text-indigo-800" data-testid={`${testid}-perday`}>
            ₹{data.suggested_rent_per_bed_per_day}/bed/day · ₹{data.annual_rent_lakhs}L/yr{showCurrent ? ` · current ${fmtINR(showCurrent.current_rent_monthly)}` : ""}
          </div>
        </>
      ) : (
        <>
          <div className="font-display font-black text-[13px] text-rose-900 mt-0.5" data-testid={`${testid}-infeasible`}>Not feasible at current revenue</div>
          <div className="text-[9.5px] text-rose-700">Increase ADR / Occ to unlock 36-mo ROI</div>
        </>
      )}
      <div className={`text-[9px] mt-0.5 ${ok ? "text-indigo-600" : "text-rose-600"}`} data-testid={`${testid}-assumption`}>{rentAssumption(tag, data, model)}</div>
    </div>
  );
}

/** Small band — Sensitivity scenario toggle. */
export function ScenarioToggle({ activeScenario, setActiveScenario }) {
  return (
    <div className="flex items-center justify-end gap-2">
      <span className="text-[11px] text-zinc-500">Sensitivity scenario:</span>
      <div className="flex bg-zinc-100 rounded-md p-0.5" data-testid="ic-scenario-toggle">
        {[{ k: "bear", l: "Bear" }, { k: "base", l: "Base" }, { k: "bull", l: "Bull" }].map(b => (
          <button key={b.k} onClick={() => setActiveScenario(b.k)} data-testid={`ic-scenario-${b.k}`} className={`text-[11px] font-medium px-2.5 py-1 rounded transition-all ${activeScenario === b.k ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}>{b.l}</button>
        ))}
      </div>
    </div>
  );
}

// Re-export Lock so the parent's locked-submit branch can stay tiny.
export { Lock };
